import type OutlineItem from './OutlineItem.js'
import { html, isServer, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { SlotController } from '../common/controllers/SlotController.js'
import { cond } from '../common/directives/cond.js'
import { NordEvent } from '../common/events.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import componentStyle from '../common/styles/Component.css'
import { LocalizeController } from '../localization/LocalizeController.js'
import style from './Outline.css'

import './OutlineItem.js'

/**
 * Event dispatched when the active section of an Outline changes, either via the
 * built-in scroll-spy, a click on one of its items, or a programmatic change.
 * The event's `activeId` is the id of the newly active heading.
 */
export class NordOutlineChangeEvent extends NordEvent {
  declare activeId: string

  constructor(activeId: string) {
    super('change')
    Object.defineProperty(this, 'activeId', {
      value: activeId,
      enumerable: true,
      writable: false,
    })
  }
}

interface ScannedHeading {
  id: string
  text: string
  level: number
}

interface OutlineEntry {
  id: string
  element: HTMLElement
  setActive: (active: boolean) => void
}

function getHeadingText(heading: HTMLElement): string {
  const clone = heading.cloneNode(true) as HTMLElement
  clone.querySelectorAll('[data-outline-ignore]').forEach(element => element.remove())
  return clone.textContent?.trim() ?? ''
}

function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

/**
 * Outline is an in-page table of contents that lists a page's headings and
 * highlights the section currently in view as you scroll.
 *
 * Provide [Outline Item](/components/outline-item/) children, or point `for` at
 * a container to build the list from its headings.
 *
 * @status draft
 * @category navigation
 *
 * @slot - The default slot for [Outline Item](/components/outline-item/) children.
 * @slot header - Custom header content shown above the list. Overrides the default localized "On this page" heading. Set `hide-header` to remove the header entirely.
 *
 * @fires {NordOutlineChangeEvent} change - Dispatched when the active section changes. Includes an `activeId` property.
 *
 * @localization label - Accessible label for the outline navigation landmark, and the default header text.
 *
 * @cssprop [--n-outline-indicator-color=var(--n-color-accent)] - Color of the sliding active indicator.
 * @cssprop [--n-outline-track-color=var(--n-color-border)] - Color of the static track behind the indicator.
 * @cssprop [--n-outline-indicator-width=2px] - Thickness of the track and indicator.
 * @cssprop [--n-outline-indent=var(--n-space-m)] - Indentation step applied per heading level.
 * @cssprop [--n-outline-header-color=var(--n-color-text-weaker)] - Color of the default header text.
 * @cssprop [--n-outline-header-font-size=var(--n-font-size-s)] - Font size of the default header text.
 */
@customElement('nord-outline')
export default class Outline extends DraftComponentMixin(LitElement) {
  static styles = [componentStyle, style]

  protected localize = new LocalizeController<'nord-outline'>(this)

  private headerSlot = new SlotController(this, 'header')

  /**
   * The id of the active heading. Setting this puts the Outline in controlled
   * mode and disables the built-in scroll-spy, so your own logic owns the
   * highlight (mirrors providing `activeId` in other libraries).
   */
  @property({ reflect: true }) active?: string

  /**
   * Tightens spacing for dense sidebars where vertical space is at a premium.
   */
  @property({ reflect: true }) density: 'default' | 'compact' = 'default'

  /**
   * DOM-scan mode: the id (or any CSS selector) of the content container whose
   * headings should populate the outline. When set, slotted children are
   * ignored and items are generated from the matched headings.
   */
  @property({ reflect: true }) for?: string

  /**
   * The selector used to find headings when `for` is set. Descendant content
   * marked with `data-outline-ignore` is omitted from generated item labels.
   */
  @property({ attribute: 'heading-selector' }) headingSelector = 'h1,h2,h3,h4'

  /**
   * CSS selector for the element that scrolls the content, used as the
   * scroll-spy `IntersectionObserver` root. Point this at your scroll container
   * when the page scrolls inside an element rather than the window — otherwise
   * the observer watches the window and never sees the headings move. If the
   * selector resolves to a `nord-scroll-area`, its scroll viewport is used.
   *
   * When unset, the nearest ancestor `nord-scroll-area` of the scanned content
   * is detected automatically, falling back to the window.
   */
  @property({ attribute: 'scroll-container' }) scrollContainer?: string

  /**
   * Hides the header. By default Outline shows a localized "On this page"
   * heading (or the `header` slot's content) above the list.
   */
  @property({ type: Boolean, reflect: true, attribute: 'hide-header' }) hideHeader = false

  @state() private scanned: ScannedHeading[] = []

  @query('.n-outline-list') private containerEl?: HTMLElement
  @query('.n-outline-indicator') private indicatorEl?: HTMLElement

  private controlled = false
  private observer?: IntersectionObserver
  private resizeObserver?: ResizeObserver
  private mutationObserver?: MutationObserver
  private readonly intersecting = new Set<string>()

  connectedCallback() {
    super.connectedCallback()

    // Expose a navigation landmark with an accessible name, without clobbering
    // any semantics the consumer has already provided.
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'navigation')
    }
    if (!this.hasAttribute('aria-label') && !this.hasAttribute('aria-labelledby')) {
      this.setAttribute('aria-label', this.localize.term('label'))
    }

    this.addEventListener('click', this.handleClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this.handleClick)
    this.observer?.disconnect()
    this.resizeObserver?.disconnect()
    this.mutationObserver?.disconnect()
  }

  protected firstUpdated() {
    if (isServer) {
      return
    }

    // Controlled mode is decided once, by whether the consumer supplied a
    // non-empty `active`. An empty value leaves scroll-spy in charge.
    this.controlled = this.active != null && this.active !== ''

    this.scanContent()

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.updateIndicator())
      this.resizeObserver.observe(this)
    }

    // Watch for slotted items being added/removed (e.g. via v-for / *ngFor).
    this.mutationObserver = new MutationObserver(() => this.refresh())
    this.mutationObserver.observe(this, { childList: true })

    this.refresh()
  }

  protected updated(changed: Map<string, unknown>) {
    if (changed.has('active')) {
      this.applyActiveToItems()
      this.updateIndicator()
    }
    if (changed.has('for') || changed.has('headingSelector')) {
      this.scanContent()
      this.refresh()
    }
    if (changed.has('scrollContainer')) {
      this.observeTargets()
    }
    if (changed.has('scanned')) {
      this.refresh()
    }
  }

  /** Re-derive entries, restart scroll-spy and reposition the indicator. */
  private refresh() {
    this.observeTargets()
    this.applyActiveToItems()
    this.updateIndicator()
  }

  /** The ordered list of items, normalised across slotted and scan modes. */
  private get entries(): OutlineEntry[] {
    if (this.for) {
      const anchors = [...this.renderRoot.querySelectorAll<HTMLAnchorElement>('a.n-outline-item')]
      return anchors.map(anchor => ({
        id: anchor.dataset.id ?? '',
        element: anchor,
        // Generated anchors get their active styling from `aria-current` in render().
        setActive: () => {},
      }))
    }

    const items = [...this.querySelectorAll<OutlineItem>('nord-outline-item')]
    return items.map(item => ({
      id: (item.getAttribute('href') ?? '').replace(/^#/, ''),
      element: item,
      setActive: (active: boolean) => {
        item.active = active
      },
    }))
  }

  private scanContent() {
    if (isServer || !this.for) {
      this.scanned = []
      return
    }

    const root = document.getElementById(this.for) ?? document.querySelector(this.for)
    if (!root) {
      this.scanned = []
      return
    }

    const headings = [...root.querySelectorAll<HTMLElement>(this.headingSelector)]
    this.scanned = headings.map((heading) => {
      const text = getHeadingText(heading)
      if (!heading.id) {
        heading.id = slugify(text)
      }
      const level = Number(heading.tagName.slice(1)) || 1
      return {
        id: heading.id,
        text,
        level: Math.min(Math.max(level, 1), 4),
      }
    })
  }

  private observeTargets() {
    this.observer?.disconnect()
    this.intersecting.clear()

    if (this.controlled || isServer || typeof IntersectionObserver === 'undefined') {
      return
    }

    // Resolve the scroll container to observe within. An explicit
    // `scroll-container` selector wins; otherwise auto-detect the nearest
    // ancestor `nord-scroll-area` of the scanned content. A `nord-scroll-area`
    // is resolved down to its scroll viewport (the element that actually
    // scrolls). When nothing resolves, `root` stays null — the observer then
    // watches the document viewport (body/html), the original behaviour.
    const explicit = this.scrollContainer ? document.querySelector(this.scrollContainer) : null
    const contentRoot = this.for
      ? (document.getElementById(this.for) ?? document.querySelector(this.for))
      : this
    const container = explicit ?? contentRoot?.closest('nord-scroll-area') ?? null

    let root: Element | null = null
    if (container) {
      if (container.localName === 'nord-scroll-area') {
        const viewport = container.shadowRoot?.querySelector('[part~="viewport"]')
        if (!viewport) {
          // The scroll-area hasn't upgraded yet; re-run once it has so we
          // observe the real scroller rather than falling back to the window.
          customElements.whenDefined('nord-scroll-area').then(() => {
            if (this.isConnected) {
              this.observeTargets()
            }
          })
          return
        }
        root = viewport
      }
      else {
        root = container
      }
    }

    // A heading becomes active as it nears the top of the container. The lower
    // bound keeps the section active while the reader is within it.
    this.observer = new IntersectionObserver(this.handleIntersect, {
      root,
      rootMargin: '0px 0px -66% 0px',
      threshold: 0,
    })

    for (const entry of this.entries) {
      const target = entry.id ? document.getElementById(entry.id) : null
      if (target) {
        this.observer.observe(target)
      }
    }
  }

  private handleIntersect = (entries: IntersectionObserverEntry[]) => {
    for (const entry of entries) {
      const id = entry.target.id
      if (entry.isIntersecting) {
        this.intersecting.add(id)
      }
      else {
        this.intersecting.delete(id)
      }
    }

    // Pick the first heading in document order that's currently in view.
    const next = this.entries.map(e => e.id).find(id => this.intersecting.has(id))
    if (next && next !== this.active) {
      this.setActive(next)
    }
  }

  private setActive(id: string) {
    this.active = id
    this.dispatchEvent(new NordOutlineChangeEvent(id))
  }

  private applyActiveToItems() {
    for (const entry of this.entries) {
      entry.setActive(entry.id === this.active)
    }
  }

  private updateIndicator() {
    const indicator = this.indicatorEl
    const container = this.containerEl
    if (!indicator || !container) {
      return
    }

    const active = this.entries.find(entry => entry.id === this.active)
    if (!active) {
      indicator.classList.remove('is-visible')
      return
    }

    const containerRect = container.getBoundingClientRect()
    const itemRect = active.element.getBoundingClientRect()
    indicator.style.transform = `translateY(${itemRect.top - containerRect.top}px)`
    indicator.style.blockSize = `${itemRect.height}px`
    indicator.classList.add('is-visible')
  }

  private handleClick = (event: MouseEvent) => {
    // Honour modifier-clicks (open in new tab, etc.) — let the browser handle them.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return
    }

    const anchor = event.composedPath().find((node): node is HTMLAnchorElement => node instanceof HTMLAnchorElement)
    const href = anchor?.getAttribute('href')
    if (!href?.startsWith('#')) {
      return
    }

    const id = href.slice(1)
    const target = document.getElementById(id)
    if (!target) {
      return
    }

    event.preventDefault()
    const reduceMotion = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })

    if (typeof history !== 'undefined') {
      history.replaceState(null, '', `#${id}`)
    }

    // Update immediately for responsiveness, even in controlled mode the click
    // is an explicit user intent so we reflect it and notify.
    this.setActive(id)
  }

  private handleSlotChange = () => {
    this.refresh()
  }

  private renderHeader() {
    if (this.hideHeader) {
      return nothing
    }
    // A custom header (slot) keeps its own semantics. The default text header is
    // hidden from assistive tech because the host's aria-label already names the
    // navigation landmark with the same term, avoiding a duplicate announcement.
    if (this.headerSlot.hasContent) {
      return html`<div class="n-outline-header" part="header"><slot name="header"></slot></div>`
    }
    return html`<div class="n-outline-header" part="header" aria-hidden="true">${this.localize.term('label')}</div>`
  }

  render() {
    return html`
      <div class="n-outline" part="base">
        ${this.renderHeader()}
        <div class="n-outline-list" part="list">
          <span class="n-outline-track" aria-hidden="true"></span>
          <span class="n-outline-indicator" aria-hidden="true"></span>
          ${this.for
            ? this.scanned.map(
                heading => html`
                  <a
                    class="n-outline-item is-generated"
                    data-id=${heading.id}
                    href="#${heading.id}"
                    level=${heading.level}
                    aria-current=${cond(this.active === heading.id, 'location')}
                    >${heading.text}</a
                  >
                `,
              )
            : html`<slot @slotchange=${this.handleSlotChange}></slot>`}
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-outline': Outline
  }
}
